'use client';

import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { cn } from '@/shared/utils/styling';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';

type DropdownMenuTriggerProps = React.ComponentProps<
  typeof DropdownMenuPrimitive.Trigger
> & {
  size?: 'sm' | 'md' | 'default';
  withChevron?: boolean;
  floatingLabel?: string;
  placeholder?: string;
  wrapperClassName?: string;
};

type DropdownMenuContentProps = React.ComponentProps<
  typeof DropdownMenuPrimitive.Content
> & {
  classNameViewport?: string;
};

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuTrigger({
  children,
  floatingLabel,
  placeholder,
  className,
  withChevron = true,
  wrapperClassName,
  ...props
}: DropdownMenuTriggerProps) {
  const hasValue = props.value !== undefined && props.value !== '';

  return (
    <div className={cn('relative', wrapperClassName)}>
      <DropdownMenuPrimitive.Trigger
        data-slot="dropdown-menu-trigger"
        data-size="md"
        className={cn(
          "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center justify-between gap-2 rounded-md bg-transparent px-3 whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-default disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          'items-start',
          'h-[6rem]',
          className
        )}
        {...props}>
        <div className="text-left">
          {floatingLabel && (
            <label
              className={cn(
                'transition-all duration-200 ease-in-out pointer-events-none text-muted-foreground ',
                hasValue ? 'p6' : 'p3'
              )}>
              {floatingLabel}
            </label>
          )}

          {!children && placeholder && (
            <label className="pointer-events-none text-muted-foreground p3">
              {placeholder}
            </label>
          )}

          {children}
        </div>

        {withChevron && (
          <ChevronDownIcon className="size-[2.4rem] text-muted-foreground" />
        )}
      </DropdownMenuPrimitive.Trigger>
    </div>
  );
}

function DropdownMenuContent({
  className,
  children,
  classNameViewport,
  ...props
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        data-side="bottom"
        side="bottom"
        className={cn(
          'bg-popover text-popover-foreground',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          'origin-(--radix-dropdown-menu-content-transform-origin)',
          'max-h-(--radix-dropdown-menu-content-available-height)',
          'min-w-[8rem] max-w-[var(--radix-dropdown-menu-trigger-width)]',
          'relative z-80',
          'rounded-md shadow-md',
          className
        )}
        {...props}>
        <div
          className={cn(
            'p-1 pt-0 w-full',
            'max-h-[var(--radix-dropdown-menu-content-available-height)]',
            'overflow-x-hidden overflow-y-auto [scrollbar-width:none]',
            'w-full min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)] scroll-my-1',
            classNameViewport
          )}>
          {children}
        </div>
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuItem({
  className,
  children,
  showOptionCheckmark = true,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  showOptionCheckmark?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        "hover:bg-accent hover:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}>
      {showOptionCheckmark && (
        <span className="absolute right-4 flex size-3.5 items-center justify-center">
          <CheckIcon className="size-7 text-primary" />
        </span>
      )}
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

type DropdownMenuItemWithSubmenuProps = {
  renderTrigger: () => React.ReactNode;
  children: React.ReactNode;
  className?: string;
  subContentClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  sideOffset?: number;
  alignOffset?: number;
  avoidCollisions?: boolean;
};

const DropdownMenuItemWithSubmenu = ({
  renderTrigger,
  className,
  onClick,
  subContentClassName,
  sideOffset = undefined,
  alignOffset = undefined,
  avoidCollisions = true,
  children,
}: DropdownMenuItemWithSubmenuProps) => {
  const [subOpen, setSubOpen] = React.useState(false);

  const handleTriggerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(e);
    if (subOpen) {
      e.preventDefault();
      setSubOpen(false);
    }
  };

  return (
    <DropdownMenuPrimitive.DropdownMenuSub
      open={subOpen}
      onOpenChange={setSubOpen}>
      <DropdownMenuPrimitive.DropdownMenuSubTrigger
        onClick={handleTriggerClick}
        className={cn(
          "hover:bg-secondary-bg [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          'data-[state=open]:bg-secondary-bg',
          className
        )}>
        {renderTrigger()}
      </DropdownMenuPrimitive.DropdownMenuSubTrigger>

      <DropdownMenuPrimitive.DropdownMenuSubContent
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        avoidCollisions={avoidCollisions}
        className={cn(subContentClassName)}>
        {children}
      </DropdownMenuPrimitive.DropdownMenuSubContent>
    </DropdownMenuPrimitive.DropdownMenuSub>
  );
};

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemWithSubmenu,
};
